import React, { useEffect, useRef, useState } from 'react';
import './AdvancedGoogleMap.css';

const AdvancedGoogleMap = ({ offices, selectedLocation, onMarkerClick }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapError, setMapError] = useState(null);
  const [isCurrentlyExpanded, setIsCurrentlyExpanded] = useState(false);

  // Extract coordinates from Google Maps URL
  const extractCoordinates = (url) => {
    if (!url) return null;

    try {
      const preciseMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
      if (preciseMatch) {
        return {
          lat: parseFloat(preciseMatch[1]),
          lng: parseFloat(preciseMatch[2]),
        };
      }

      const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (coordMatch) {
        return {
          lat: parseFloat(coordMatch[1]),
          lng: parseFloat(coordMatch[2]),
        };
      }

      const queryMatch = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (queryMatch) {
        return {
          lat: parseFloat(queryMatch[1]),
          lng: parseFloat(queryMatch[2]),
        };
      }

      return null;
    } catch (error) {
      console.error('Error extracting coordinates:', error);
      return null;
    }
  };

  // Build marker with both simple icon and details (details hidden by default)
  const buildMarkerContent = (office) => {
    const content = document.createElement('div');
    content.classList.add('office-marker-container');
    content.style.position = 'relative';

    content.innerHTML = `
      <div class="office-marker-simple">
        <div class="simple-marker-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="92" height="92" viewBox="0 0 24 24" fill="currentColor" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
            <circle cx="12" cy="10" r="3" fill="white"></circle>
          </svg>
        </div>
      </div>
      <div class="office-marker-detailed" style="display: none; position: absolute; bottom: 100%; left: 150%; transform: translate(-140% , 60%); margin-bottom: 10px;">
        <button class="marker-close-btn" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div class="marker-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </div>
        <div class="marker-details">
          <div class="office-name">${office.name}</div>
          ${office.address ? `<div class="office-address">${office.address}</div>` : ''}
          ${office.city && office.city !== office.name ? `<div class="office-city">${office.city}</div>` : ''}
          ${office.country ? `<div class="office-country">${office.country}</div>` : ''}
          ${office.phone ? `
            <div class="office-contact">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              <span>${office.phone}</span>
            </div>
          ` : ''}
          ${office.email ? `
            <div class="office-contact">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
              </svg>
              <span>${office.email}</span>
            </div>
          ` : ''}
          ${office.openingHours ? `
            <div class="office-contact">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>${office.openingHours}</span>
            </div>
          ` : ''}
        </div>
      </div>
    `;

    return content; 
  };

  // Toggle marker between simple and detailed view
  const toggleMarkerView = (marker, office, forceCollapse = false) => {
    const detailedPanel = marker.content.querySelector('.office-marker-detailed');
    const isExpanded = detailedPanel.style.display !== 'none';
   
    // Collapse all other markers
    markersRef.current.forEach((m) => {
      if (m !== marker && m.content) {
        const otherDetailedPanel = m.content.querySelector('.office-marker-detailed');
        if (otherDetailedPanel) {
          otherDetailedPanel.style.display = 'none';
        }
        m.zIndex = null;
      }
    });

    if (forceCollapse || isExpanded) {
      // Collapse this marker
      detailedPanel.style.display = 'none';
      marker.zIndex = null;
      setIsCurrentlyExpanded(false);
    } else {
      // Expand this marker
      detailedPanel.style.display = 'block';
      marker.zIndex = 1;
      setIsCurrentlyExpanded(true);

      // Notify parent component
      if (onMarkerClick) {
        onMarkerClick(office);
      }
    }
  };

  useEffect(() => {
    const initMap = async () => {
      if (!window.google || !window.google.maps) {
        setMapError('Google Maps API is not loaded. Please check your API key.');
        return;
      }

      if (!mapRef.current) return;

      try {
        const google = window.google;

        const validOffices = offices
          .map((office) => ({
            ...office,
            position: extractCoordinates(office.googleMapUrl),
          }))
          .filter((office) => office.position);

        if (validOffices.length === 0) {
          setMapError('No valid coordinates found in office data.');
          return;
        }

        // Determine initial center based on selected location
        let initialCenter;
        let initialZoom = 14; // Zoomed in view

        if (selectedLocation) {
          // Find the selected office
          const selectedOffice = validOffices.find(office => office.name === selectedLocation);
          if (selectedOffice) {
            initialCenter = selectedOffice.position;
          } else {
            // Fallback to first office
            initialCenter = validOffices[0].position;
          }
        } else {
          // Default to first office
          initialCenter = validOffices[0].position;
        }

        const { Map } = await google.maps.importLibrary('maps');
        const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

        const map = new Map(mapRef.current, {
          zoom: initialZoom,
          center: initialCenter,
          mapId: 'kangaroo-offices-map',
        });

        mapInstanceRef.current = map;

        markersRef.current.forEach((marker) => {
          marker.map = null;
        });
        markersRef.current = [];

        validOffices.forEach((office) => {
          // Create marker with both simple and detailed content
          const markerContent = buildMarkerContent(office);

          const marker = new AdvancedMarkerElement({
            map,
            position: office.position,
            content: markerContent,
            title: office.name,
          });

          // Store office data in marker for later reference
          marker.officeData = office;

          // Add click listener to the simple marker icon
          const simpleMarker = markerContent.querySelector('.office-marker-simple');
          simpleMarker.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleMarkerView(marker, office);
          });

          // Add click listener to close button
          const closeBtn = markerContent.querySelector('.marker-close-btn');
          if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              toggleMarkerView(marker, office, true);
            });
          }

          markersRef.current.push(marker);

          // Auto-expand marker on initial load if it matches selectedLocation
          if (selectedLocation && office.name === selectedLocation) {
            setTimeout(() => {
              const detailedPanel = markerContent.querySelector('.office-marker-detailed');
              if (detailedPanel) {
                detailedPanel.style.display = 'block';
                marker.zIndex = 1;
                setIsCurrentlyExpanded(true);
                
                if (onMarkerClick) {
                  onMarkerClick(office);
                }
              }
            }, 100);
          }
        });

        setMapError(null);
      } catch (error) {
        console.error('Map initialization error:', error);
        setMapError(`Error loading map: ${error.message}`);
      }
    };

    initMap();

    return () => {
      markersRef.current.forEach((marker) => {
        marker.map = null;
      });
      markersRef.current = [];
    };
  }, [offices, selectedLocation, onMarkerClick]);

  if (mapError) {
    return (
      <div className="map-error">
        <p>⚠️ {mapError}</p>
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      id="map"
      style={{ width: '100%', height: '100%', minHeight: '400px' }}
    />
  );
};

export default AdvancedGoogleMap;